/**
 * PatternStore — local-storage-backed CRUD for Sequencer patterns.
 *
 * Patterns are smaller and more iterative than full presets, so they
 * deserve their own lightweight persistence. No Supabase round-trip:
 * patterns live entirely in the browser. If the user signs in on
 * another device they'd save patterns again there — that trade is
 * worth keeping the store dependency-free and instant.
 *
 * Storage shape (single localStorage key, JSON-encoded):
 *
 *   {
 *     "patterns": [
 *       { "id": "pat-...", "name": "Hi-hat shuffle", ... },
 *       ...
 *     ]
 *   }
 */

import type { SequencerStep } from './Sequencer';

const STORAGE_KEY = 'evil-synth/sequencer-patterns';
const PATTERN_SCHEMA_VERSION = 2;

/** Display category used when a pattern doesn't specify one. Same
 *  convention as the preset browser. */
export const DEFAULT_PATTERN_CATEGORY = 'Uncategorized';

/** Starter category names that always appear in the dropdown so a
 *  blank store still suggests sensible buckets. Mirrors preset
 *  starters, tilted toward sequencer use. */
export const STARTER_PATTERN_CATEGORIES: readonly string[] = [
  'Bass',
  'Lead',
  'Drums',
  'Pad',
  'Arp',
  'FX',
];

export interface PatternData {
  /** Stable identifier minted at save time. */
  readonly id: string;
  /** User-supplied display name. */
  readonly name: string;
  /** Category label for dropdown grouping. v2+ field — older patterns
   *  get migrated to DEFAULT_PATTERN_CATEGORY on load. */
  readonly category: string;
  /** ms-since-epoch — milliseconds from `Date.now()`. */
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Sequencer state captured at save time. */
  readonly schemaVersion: number;
  readonly rate: string;
  readonly length: number;
  readonly swing: number;
  readonly steps: readonly SequencerStep[];
}

export type PatternListener = (patterns: readonly PatternData[]) => void;

export class PatternStore {
  /**
   * In-memory cache. Subclasses (e.g. a Supabase-backed one) need
   * read+write access to merge cloud rows into the cache without
   * a duplicate parallel array. Direct mutation should still go
   * through `put` / `update` / `delete` so listeners stay in sync;
   * subclasses bulk-merging via `mergePatterns()` is the supported
   * extension point.
   */
  protected patterns: PatternData[] = [];
  protected readonly listeners = new Set<PatternListener>();

  constructor() {
    this.load();
  }

  /** Most-recently-updated first; stable for repeat reads. */
  list(): readonly PatternData[] {
    return [...this.patterns].sort((a, b) => b.updatedAt - a.updatedAt);
  }

  get(id: string): PatternData | null {
    return this.patterns.find((p) => p.id === id) ?? null;
  }

  /** Insert a fresh pattern. Returns the assigned id. Missing
   *  `category` defaults to DEFAULT_PATTERN_CATEGORY so the saved
   *  pattern always lands in a known bucket. */
  put(
    pattern: Omit<
      PatternData,
      'id' | 'createdAt' | 'updatedAt' | 'schemaVersion' | 'category'
    > & { category?: string },
  ): string {
    const now = Date.now();
    const id = mintId();
    const next: PatternData = {
      ...pattern,
      id,
      category: pattern.category?.trim() || DEFAULT_PATTERN_CATEGORY,
      schemaVersion: PATTERN_SCHEMA_VERSION,
      createdAt: now,
      updatedAt: now,
    };
    this.patterns.push(next);
    this.persist();
    this.emit();
    return id;
  }

  /**
   * Distinct category names across the bank, sorted for stable
   * display. The starter list is unioned in so a fresh store still
   * suggests sensible buckets. DEFAULT_PATTERN_CATEGORY is always
   * present.
   */
  categories(): string[] {
    const seen = new Set<string>([DEFAULT_PATTERN_CATEGORY]);
    for (const c of STARTER_PATTERN_CATEGORIES) seen.add(c);
    for (const p of this.patterns) {
      if (p.category) seen.add(p.category);
    }
    return [...seen].sort();
  }

  /**
   * Overwrite an existing pattern's contents while keeping its id +
   * createdAt. No-op when the id isn't found. Use this to "save
   * over" a loaded pattern without ballooning the list with copies.
   */
  update(
    id: string,
    patch: Partial<Omit<PatternData, 'id' | 'createdAt'>>,
  ): boolean {
    const idx = this.patterns.findIndex((p) => p.id === id);
    if (idx < 0) return false;
    const existing = this.patterns[idx]!;
    const next: PatternData = {
      ...existing,
      ...patch,
      id: existing.id,
      createdAt: existing.createdAt,
      updatedAt: Date.now(),
    };
    this.patterns[idx] = next;
    this.persist();
    this.emit();
    return true;
  }

  delete(id: string): boolean {
    const idx = this.patterns.findIndex((p) => p.id === id);
    if (idx < 0) return false;
    this.patterns.splice(idx, 1);
    this.persist();
    this.emit();
    return true;
  }

  /** Drop every pattern. Used in tests + a hypothetical "reset" path. */
  clear(): void {
    if (this.patterns.length === 0) return;
    this.patterns = [];
    this.persist();
    this.emit();
  }

  subscribe(fn: PatternListener): () => void {
    this.listeners.add(fn);
    fn(this.list());
    return () => {
      this.listeners.delete(fn);
    };
  }

  // ── Subclass extension points ─────────────────────────────────────────

  /**
   * Merge a batch of patterns into the in-memory cache. For each
   * incoming entry, the entry with the higher `updatedAt` wins; if
   * the local entry is newer it stays. Persists + emits at the end.
   *
   * Used by Supabase-backed subclasses to fold a cloud `list()`
   * result into the local cache on session restore. Public so
   * subclasses don't have to reach into protected internals to
   * reuse the merge semantics.
   */
  mergePatterns(incoming: readonly PatternData[]): void {
    if (incoming.length === 0) return;
    let changed = false;
    const byId = new Map<string, PatternData>();
    for (const p of this.patterns) byId.set(p.id, p);
    for (const remote of incoming) {
      const local = byId.get(remote.id);
      if (!local || remote.updatedAt > local.updatedAt) {
        byId.set(remote.id, remote);
        changed = true;
      }
    }
    if (!changed) return;
    this.patterns = [...byId.values()];
    this.persist();
    this.emit();
  }

  // ── Internals ─────────────────────────────────────────────────────────

  protected load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { patterns?: unknown };
      if (!Array.isArray(parsed.patterns)) return;
      // Validate each entry — corrupted JSON shouldn't crash the boot.
      // Older entries (v1) lack `category`; migrate them to the
      // default bucket so the load succeeds and the user can
      // recategorize from the modal afterward.
      const filtered: PatternData[] = [];
      for (const p of parsed.patterns) {
        const migrated = migrateLoadedPattern(p);
        if (migrated) filtered.push(migrated);
      }
      this.patterns = filtered;
    } catch {
      /* localStorage unavailable / corrupted — start fresh */
    }
  }

  protected persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ patterns: this.patterns }),
      );
    } catch {
      /* quota / private mode — silently drop the write */
    }
  }

  protected emit(): void {
    const snapshot = this.list();
    for (const fn of this.listeners) fn(snapshot);
  }
}

function mintId(): string {
  // Compact, sortable-ish, collision-resistant for browser-only use.
  return `pat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isValidPattern(p: unknown): p is PatternData {
  if (!p || typeof p !== 'object') return false;
  const candidate = p as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.createdAt === 'number' &&
    typeof candidate.updatedAt === 'number' &&
    typeof candidate.rate === 'string' &&
    typeof candidate.length === 'number' &&
    typeof candidate.swing === 'number' &&
    Array.isArray(candidate.steps)
  );
}

/**
 * Validate + migrate a single persisted pattern entry. v1 entries
 * lack `category`; we drop them into DEFAULT_PATTERN_CATEGORY and
 * bump the schemaVersion in-memory so future writes save the v2
 * shape. Returns null when the entry is malformed beyond rescue.
 */
function migrateLoadedPattern(raw: unknown): PatternData | null {
  if (!isValidPattern(raw)) return null;
  // Cast to a mutable shape so we can fill in missing fields.
  const r = raw as PatternData & { category?: unknown };
  const category =
    typeof r.category === 'string' && r.category.trim()
      ? r.category
      : DEFAULT_PATTERN_CATEGORY;
  return {
    ...r,
    category,
    schemaVersion: PATTERN_SCHEMA_VERSION,
  };
}
